import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const CATS = [
    {
        label: "Socks",
        cat: "socks",
        children: [
            { label: "Men", to: "/shop/socks/men" },
            { label: "Women", to: "/shop/socks/women" },
        ],
    },
    {
        label: "Boxers",
        cat: "boxers",
        children: [
            { label: "Men", to: "/shop/boxers/men" },
            { label: "Women", to: "/shop/boxers/women" },
        ],
    },
    {
        label: "Undershirts",
        cat: "undershirts",
        children: [
            { label: "Men", to: "/shop/undershirts/men" },
            { label: "Women", to: "/shop/undershirts/women" },
        ],
    },
    {
        label: "Essentials",
        cat: "essentials",
        children: [
            { label: "Men", to: "/shop/essentials/men" },
            { label: "Women", to: "/shop/essentials/women" },
        ],
    },
];

export const Navbar = () => {
    const { count } = useCart();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [hover, setHover] = useState(null);
    const location = useLocation();

    useEffect(() => setOpen(false), [location.pathname]);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`fixed top-[38px] inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-paper/85 backdrop-blur-xl border-b border-line"
                    : "bg-transparent border-b border-transparent"
            }`}
            data-testid="navbar"
        >
            <nav className="max-w-[1500px] mx-auto px-5 sm:px-8 h-24 md:h-32 flex items-center justify-between">
                <Logo />

                <div className="hidden md:flex items-center gap-10">
                    <Link
                        to="/"
                        data-testid="nav-home"
                        className={`eyebrow link-underline ${
                            location.pathname === "/" ? "text-ink" : "text-ink/70 hover:text-ink"
                        }`}
                    >
                        Home
                    </Link>
                    {CATS.map((c) => (
                        <div
                            key={c.cat}
                            className="relative"
                            onMouseEnter={() => setHover(c.cat)}
                            onMouseLeave={() => setHover(null)}
                        >
                            <button
                                className="eyebrow text-ink/70 hover:text-ink flex items-center gap-1"
                                data-testid={`nav-${c.cat}`}
                            >
                                {c.label}
                                <ChevronDown size={12} strokeWidth={1.5} />
                            </button>
                            <AnimatePresence>
                                {hover === c.cat && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 8 }}
                                        transition={{ duration: 0.25 }}
                                        className="absolute left-0 top-full pt-4"
                                    >
                                        <div className="bg-paper border border-line min-w-[160px] py-2">
                                            {c.children.map((ch) => (
                                                <Link
                                                    key={ch.to}
                                                    to={ch.to}
                                                    data-testid={`nav-${c.cat}-${ch.label.toLowerCase()}`}
                                                    className="block px-5 py-3 text-sm text-ink/70 hover:text-ink hover:bg-secondary transition-colors"
                                                >
                                                    {ch.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                    <Link
                        to="/philosophy"
                        data-testid="nav-philosophy"
                        className={`eyebrow link-underline ${
                            location.pathname === "/philosophy" ? "text-ink" : "text-ink/70 hover:text-ink"
                        }`}
                    >
                        Our Philosophy
                    </Link>
                </div>

                <div className="flex items-center gap-5">
                    <Link
                        to={user ? "/account" : "/login"}
                        data-testid="nav-account"
                        className="inline-flex text-ink/70 hover:text-ink transition-colors"
                        aria-label="Account"
                    >
                        <User size={18} strokeWidth={1.5} />
                    </Link>
                    <Link
                        to="/cart"
                        data-testid="nav-cart"
                        className="relative eyebrow text-ink hover:opacity-70 transition-opacity"
                        aria-label="Cart"
                    >
                        Cart
                        <span
                            data-testid="cart-count"
                            className="ml-2 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 border border-ink text-[0.62rem] tabular-nums"
                        >
                            {count}
                        </span>
                    </Link>
                    <button
                        className="md:hidden text-ink"
                        onClick={() => setOpen((o) => !o)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden bg-paper border-b border-line overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-8 flex flex-col gap-6">
                            <Link to="/" className="font-serif text-3xl text-ink">Home</Link>
                            {CATS.map((c) => (
                                <div key={c.cat}>
                                    <p className="font-serif text-3xl text-ink">{c.label}</p>
                                    <div className="mt-2 ml-1 flex flex-col gap-2">
                                        {c.children.map((ch) => (
                                            <Link
                                                key={ch.to}
                                                to={ch.to}
                                                data-testid={`mobile-nav-${c.cat}-${ch.label.toLowerCase()}`}
                                                className="text-base text-ink/60"
                                            >
                                                {ch.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <Link to="/philosophy" className="font-serif text-3xl text-ink">
                                Our Philosophy
                            </Link>
                            <Link
                                to={user ? "/account" : "/login"}
                                className="font-serif text-3xl text-ink/60"
                            >
                                {user ? "Account" : "Sign In"}
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
